/**
 * #630 Supervisor AI interpretation v1: a configured role AI's advisory reading of ONE
 * deterministic Supervisor report.
 *
 * The Supervisors are retired: nothing asks a model for an interpretation or publishes a new
 * one. What is here reads and validates the interpretations already on the report stream, so
 * that history stays readable.
 *
 * The deterministic report stays canonical evidence. This is a separate record beside it:
 * - it is tied to that report by `reportCorrelationId` and never changes or replaces it;
 * - it may cite only evidence/fact ids the report carries - it cannot add evidence;
 * - it carries the execution-derived Actual identity (as node_report_role_execution
 *   recorded it) and the #618 bundle/playbook versions actually consumed;
 * - it is advisory (`executionAuthority: false`) and grants nothing.
 *
 * It rides the existing report stream as a `supervisor_synthesis` envelope with its own
 * protocol and its own stream key (`supervisor:<role>:ai`), so the deterministic report
 * stays each Supervisor's latest and its dedupe is untouched.
 */

import { ReportValidationError, SECRET_SHAPED_PAYLOAD_KEYS, type ReportEnvelope } from '../streams/report.js';
import {
  CONFIDENCE_STATES,
  assertSupervisorReportWellFormed,
  type ConfidenceState,
  type SupervisorReport,
} from './report.js';

/**
 * v2 (#630) adds routeConfiguredAt: the route configuration generation the perspective was produced
 * under, so "same report + same route generation + same bundle/playbook" is one durable input and
 * a restart never spends on it again. v1 perspectives stay readable.
 */
export const SUPERVISOR_AI_INTERPRETATION_VERSION = 2 as const;
/** The report stream's envelope payloadVersion (publish_report_message accepts 1). */
const ENVELOPE_PAYLOAD_VERSION = 1 as const;
export const SUPERVISOR_AI_PROTOCOL = 'supervisor_ai_interpretation' as const;

/** The Supervisor roles a configured AI can run for (#637 node_role_route roles). */
export const SUPERVISOR_AI_ROLES = ['machinist', 'watchdog', 'project_keeper', 'qa_reviewer'] as const;
export type SupervisorAiRole = (typeof SUPERVISOR_AI_ROLES)[number];

/** Management roles a Supervisor AI may ask to review its perspective (#618 playbook roles). */
export const SUPERVISOR_REVIEW_ROLES = [
  'overseer', 'observer_ux', 'observer_core',
  'machinist', 'watchdog', 'project_keeper', 'qa_reviewer',
] as const;
export type SupervisorReviewRole = (typeof SUPERVISOR_REVIEW_ROLES)[number];

export const SUPERVISOR_AI_LIMITS = {
  interpretationChars: 1_200,
  textChars: 1_200,
  alternatives: 4,
  alternativeChars: 600,
  unknowns: 8,
  unknownChars: 400,
  reviewRequests: 3,
  reviewReasonChars: 400,
  citations: 48,
  serializedChars: 16_000,
} as const;

/** Exactly what the configured role AI returns. Anything else is refused, never repaired. */
export interface SupervisorAiOutput {
  readonly interpretation: string;
  readonly concern: string | null;
  readonly recommendation: string | null;
  readonly alternatives: readonly string[];
  readonly ideaOpportunity: string | null;
  readonly confidence: ConfidenceState;
  readonly unknowns: readonly string[];
  readonly proposedNextProofOrAction: string | null;
  readonly requestRoleReview: readonly { readonly role: SupervisorReviewRole; readonly reason: string }[];
  /** Evidence reference ids of the supplied report this perspective rests on. */
  readonly evidenceRefIds: readonly string[];
  /** Fact ids of the supplied report this perspective rests on. */
  readonly factIds: readonly string[];
}

export const SUPERVISOR_AI_OUTPUT_KEYS = [
  'interpretation', 'concern', 'recommendation', 'alternatives', 'ideaOpportunity', 'confidence',
  'unknowns', 'proposedNextProofOrAction', 'requestRoleReview', 'evidenceRefIds', 'factIds',
] as const;

/** What actually ran, as the execution report recorded it. Never filled from configuration. */
export interface SupervisorAiExecution {
  readonly via: 'primary' | 'fallback';
  readonly offeringKey: string;
  readonly runtimeKey: string;
  readonly modelKey: string | null;
  readonly effortKey: string | null;
  readonly verification: 'matched' | 'partially_unknown';
}

export interface SupervisorAiInterpretation extends SupervisorAiOutput {
  readonly interpretationVersion: 1 | typeof SUPERVISOR_AI_INTERPRETATION_VERSION;
  /** v2: node_role_route.configuredAt (the binding's updated_at) exactly as the database gave it. */
  readonly routeConfiguredAt?: string;
  readonly role: SupervisorAiRole;
  readonly supervisor: { readonly id: string; readonly role: string };
  readonly reportCorrelationId: string;
  readonly execution: SupervisorAiExecution;
  readonly bundleVersion: string;
  readonly playbookVersion: string | null;
  readonly createdAt: string;
  readonly correlationId: string;
  readonly executionAuthority: false;
}

const INTERPRETATION_KEYS_V1 = [
  ...SUPERVISOR_AI_OUTPUT_KEYS,
  'interpretationVersion', 'role', 'supervisor', 'reportCorrelationId', 'execution',
  'bundleVersion', 'playbookVersion', 'createdAt', 'correlationId', 'executionAuthority',
] as const;
const INTERPRETATION_KEYS_V2 = [...INTERPRETATION_KEYS_V1, 'routeConfiguredAt'] as const;
const keysFor = (version: unknown): readonly string[] => (version === 1 ? INTERPRETATION_KEYS_V1 : INTERPRETATION_KEYS_V2);

const REGISTRY_KEY = /^[A-Za-z0-9._:/@-]{1,128}$/;
const MODEL_KEY = /^[A-Za-z0-9._:/@+[\]-]{1,128}$/;
const EFFORT_KEY = /^[a-z][a-z0-9_-]{0,31}$/;
const VERSION_KEY = /^[A-Za-z0-9._:@-]{1,64}$/;
const ISO = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:\d{2})$/;
const AI_CORRELATION = /^supervisor-ai-v1:[0-9a-f]{16}$/;
/** A Postgres timestamptz as JSON: ISO date, time with optional fraction, and an offset. */
const ROUTE_GENERATION = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{1,6})?(Z|[+-]\d{2}(:\d{2})?)$/;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
function fail(message: string): never {
  throw new ReportValidationError(message);
}
function exactKeys(value: Record<string, unknown>, keys: readonly string[], field: string): void {
  const allowed = new Set(keys);
  for (const key of Object.keys(value)) if (!allowed.has(key)) fail(`${field} contains unsupported field '${key}'`);
  for (const key of keys) if (!(key in value)) fail(`${field} is missing '${key}'`);
}
function text(value: unknown, field: string, max: number): string {
  if (typeof value !== 'string' || value.trim() === '') fail(`${field} must be a non-blank string`);
  if (value.length > max) fail(`${field} exceeds ${max} characters`);
  return value;
}
function optionalText(value: unknown, field: string, max: number): string | null {
  return value === null ? null : text(value, field, max);
}
function textList(value: unknown, field: string, maxItems: number, maxChars: number): string[] {
  if (!Array.isArray(value) || value.length > maxItems) fail(`${field} must be an array of at most ${maxItems}`);
  const out = value.map((item, index) => text(item, `${field}[${index}]`, maxChars));
  if (new Set(out).size !== out.length) fail(`${field} must not repeat itself`);
  return out;
}
function noSecretKeys(value: unknown, path: string): void {
  if (Array.isArray(value)) { value.forEach((item, index) => noSecretKeys(item, `${path}[${index}]`)); return; }
  if (!isRecord(value)) return;
  for (const [key, nested] of Object.entries(value)) {
    if (SECRET_SHAPED_PAYLOAD_KEYS.some((term) => key.toLowerCase().includes(term))) fail(`${path} field '${key}' is secret-shaped`);
    noSecretKeys(nested, `${path}.${key}`);
  }
}

/**
 * Validate the model's structured output against the ONE report it was given. Every cited
 * id must be an id that report carries: the AI can interpret evidence, never add it. A report
 * with facts must be cited, so a perspective cannot float free of the evidence.
 */
export function assertSupervisorAiOutput(value: unknown, report: SupervisorReport): SupervisorAiOutput {
  if (!isRecord(value)) fail('Supervisor AI output must be one JSON object');
  exactKeys(value, SUPERVISOR_AI_OUTPUT_KEYS, 'Supervisor AI output');
  noSecretKeys(value, 'Supervisor AI output');
  const L = SUPERVISOR_AI_LIMITS;
  if (!CONFIDENCE_STATES.includes(value['confidence'] as ConfidenceState)) fail('Supervisor AI output confidence is unsupported');
  const reviews = value['requestRoleReview'];
  if (!Array.isArray(reviews) || reviews.length > L.reviewRequests) fail(`requestRoleReview must be an array of at most ${L.reviewRequests}`);
  const requestRoleReview = reviews.map((entry, index) => {
    if (!isRecord(entry)) fail(`requestRoleReview[${index}] must be an object`);
    exactKeys(entry, ['role', 'reason'], `requestRoleReview[${index}]`);
    if (!SUPERVISOR_REVIEW_ROLES.includes(entry['role'] as SupervisorReviewRole)) fail(`requestRoleReview[${index}].role is not a management role`);
    return { role: entry['role'] as SupervisorReviewRole, reason: text(entry['reason'], `requestRoleReview[${index}].reason`, L.reviewReasonChars) };
  });
  if (new Set(requestRoleReview.map((entry) => entry.role)).size !== requestRoleReview.length) fail('requestRoleReview names a role twice');
  if (requestRoleReview.some((entry) => `supervisor:${entry.role}` === report.supervisor.id)) fail('requestRoleReview cannot ask the reporting role itself');

  const evidenceRefIds = textList(value['evidenceRefIds'], 'evidenceRefIds', L.citations, 200);
  const factIds = textList(value['factIds'], 'factIds', L.citations, 200);
  const knownEvidence = new Set(report.evidenceRefs.map((ref) => ref.id));
  const knownFacts = new Set(report.facts.map((fact) => fact.id));
  const invented = [...evidenceRefIds.filter((id) => !knownEvidence.has(id)), ...factIds.filter((id) => !knownFacts.has(id))];
  if (invented.length > 0) fail(`Supervisor AI output cites evidence the report does not carry: ${invented.slice(0, 3).join(', ')}`);
  if (report.facts.length > 0 && evidenceRefIds.length === 0 && factIds.length === 0) {
    fail('Supervisor AI output cites none of the supplied facts or evidence');
  }
  return {
    interpretation: text(value['interpretation'], 'interpretation', L.interpretationChars),
    concern: optionalText(value['concern'], 'concern', L.textChars),
    recommendation: optionalText(value['recommendation'], 'recommendation', L.textChars),
    alternatives: textList(value['alternatives'], 'alternatives', L.alternatives, L.alternativeChars),
    ideaOpportunity: optionalText(value['ideaOpportunity'], 'ideaOpportunity', L.textChars),
    confidence: value['confidence'] as ConfidenceState,
    unknowns: textList(value['unknowns'], 'unknowns', L.unknowns, L.unknownChars),
    proposedNextProofOrAction: optionalText(value['proposedNextProofOrAction'], 'proposedNextProofOrAction', L.textChars),
    requestRoleReview,
    evidenceRefIds,
    factIds,
  };
}

function fnv1a32(value: string, seed: number): string {
  let hash = seed >>> 0;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16_777_619) >>> 0;
  }
  return hash.toString(16).padStart(8, '0');
}

type InterpretationMaterial = Omit<SupervisorAiInterpretation, 'createdAt' | 'correlationId' | 'executionAuthority'>;

function correlationFor(material: InterpretationMaterial): string {
  // Same perspective on the same report from the same execution identity = same id (dedupe).
  const source = JSON.stringify(keysFor(material.interpretationVersion).filter((key) => key !== 'createdAt' && key !== 'correlationId' && key !== 'executionAuthority')
    .map((key) => [key, (material as unknown as Record<string, unknown>)[key]]));
  return `supervisor-ai-v1:${fnv1a32(source, 2_166_136_261)}${fnv1a32(source, 3_335_557_777)}`;
}

/**
 * Build one interpretation record. No host calls this any more; it stays because the tests of
 * the history readers build the stored record they read with it.
 */
export function createSupervisorAiInterpretation(input: {
  readonly role: SupervisorAiRole;
  readonly report: SupervisorReport;
  readonly routeConfiguredAt: string;
  readonly output: SupervisorAiOutput;
  readonly execution: SupervisorAiExecution;
  readonly bundleVersion: string;
  readonly playbookVersion: string | null;
  readonly createdAt: string;
}): SupervisorAiInterpretation {
  assertSupervisorReportWellFormed(input.report);
  const output = assertSupervisorAiOutput(input.output, input.report);
  const material: InterpretationMaterial = {
    interpretationVersion: SUPERVISOR_AI_INTERPRETATION_VERSION,
    role: input.role,
    supervisor: { id: input.report.supervisor.id, role: input.report.supervisor.role },
    reportCorrelationId: input.report.correlationId,
    ...output,
    execution: { ...input.execution },
    bundleVersion: input.bundleVersion,
    playbookVersion: input.playbookVersion,
    routeConfiguredAt: input.routeConfiguredAt,
  };
  const interpretation: SupervisorAiInterpretation = {
    ...material,
    createdAt: input.createdAt,
    correlationId: correlationFor(material),
    executionAuthority: false,
  };
  assertSupervisorAiInterpretationWellFormed(interpretation);
  return interpretation;
}

export function assertSupervisorAiInterpretationWellFormed(value: unknown): asserts value is SupervisorAiInterpretation {
  if (!isRecord(value)) fail('Supervisor AI interpretation must be an object');
  if (value['interpretationVersion'] !== 1 && value['interpretationVersion'] !== SUPERVISOR_AI_INTERPRETATION_VERSION) {
    fail('Supervisor AI interpretation version is unsupported');
  }
  exactKeys(value, keysFor(value['interpretationVersion']), 'Supervisor AI interpretation');
  noSecretKeys(value, 'Supervisor AI interpretation');
  if (value['interpretationVersion'] === SUPERVISOR_AI_INTERPRETATION_VERSION
    && (typeof value['routeConfiguredAt'] !== 'string' || !ROUTE_GENERATION.test(value['routeConfiguredAt']) || Number.isNaN(Date.parse(value['routeConfiguredAt'])))) {
    fail('routeConfiguredAt must be the route configuration timestamp');
  }
  if (value['executionAuthority'] !== false) fail('Supervisor AI interpretations are advisory and carry no execution authority');
  const role = value['role'];
  if (!SUPERVISOR_AI_ROLES.includes(role as SupervisorAiRole)) fail('Supervisor AI interpretation role is not a Supervisor role');
  const supervisor = value['supervisor'];
  if (!isRecord(supervisor)) fail('supervisor must be an object');
  exactKeys(supervisor, ['id', 'role'], 'supervisor');
  if (supervisor['id'] !== `supervisor:${String(role)}`) fail('supervisor.id does not match the interpreted role');
  text(supervisor['role'], 'supervisor.role', 200);
  if (typeof value['reportCorrelationId'] !== 'string' || !/^supervisor-report-v1:[0-9a-f]{16}$/.test(value['reportCorrelationId'])) {
    fail('reportCorrelationId must name a Supervisor report v1');
  }
  const execution = value['execution'];
  if (!isRecord(execution)) fail('execution must be an object');
  exactKeys(execution, ['via', 'offeringKey', 'runtimeKey', 'modelKey', 'effortKey', 'verification'], 'execution');
  if (execution['via'] !== 'primary' && execution['via'] !== 'fallback') fail('execution.via must be primary or fallback');
  if (typeof execution['offeringKey'] !== 'string' || !REGISTRY_KEY.test(execution['offeringKey'])) fail('execution.offeringKey is invalid');
  if (typeof execution['runtimeKey'] !== 'string' || !REGISTRY_KEY.test(execution['runtimeKey'])) fail('execution.runtimeKey is invalid');
  if (execution['modelKey'] !== null && (typeof execution['modelKey'] !== 'string' || !MODEL_KEY.test(execution['modelKey']))) fail('execution.modelKey is invalid');
  if (execution['effortKey'] !== null && (typeof execution['effortKey'] !== 'string' || !EFFORT_KEY.test(execution['effortKey']))) fail('execution.effortKey is invalid');
  if (execution['verification'] !== 'matched' && execution['verification'] !== 'partially_unknown') {
    fail('only a matched or partially_unknown execution may be published');
  }
  if (typeof value['bundleVersion'] !== 'string' || !VERSION_KEY.test(value['bundleVersion'])) fail('bundleVersion is invalid');
  if (value['playbookVersion'] !== null && (typeof value['playbookVersion'] !== 'string' || !VERSION_KEY.test(value['playbookVersion']))) {
    fail('playbookVersion is invalid');
  }
  if (typeof value['createdAt'] !== 'string' || !ISO.test(value['createdAt']) || Number.isNaN(Date.parse(value['createdAt']))) {
    fail('createdAt must be an ISO timestamp');
  }
  if (typeof value['correlationId'] !== 'string' || !AI_CORRELATION.test(value['correlationId'])) fail('correlationId is invalid');
  // The output fields: shape only here (citations were checked against the report when created).
  const L = SUPERVISOR_AI_LIMITS;
  text(value['interpretation'], 'interpretation', L.interpretationChars);
  for (const key of ['concern', 'recommendation', 'ideaOpportunity', 'proposedNextProofOrAction'] as const) optionalText(value[key], key, L.textChars);
  textList(value['alternatives'], 'alternatives', L.alternatives, L.alternativeChars);
  textList(value['unknowns'], 'unknowns', L.unknowns, L.unknownChars);
  textList(value['evidenceRefIds'], 'evidenceRefIds', L.citations, 200);
  textList(value['factIds'], 'factIds', L.citations, 200);
  if (!CONFIDENCE_STATES.includes(value['confidence'] as ConfidenceState)) fail('confidence is unsupported');
  const reviews = value['requestRoleReview'];
  if (!Array.isArray(reviews) || reviews.length > L.reviewRequests) fail('requestRoleReview is invalid');
  for (const [index, entry] of reviews.entries()) {
    if (!isRecord(entry)) fail(`requestRoleReview[${index}] must be an object`);
    exactKeys(entry, ['role', 'reason'], `requestRoleReview[${index}]`);
    if (!SUPERVISOR_REVIEW_ROLES.includes(entry['role'] as SupervisorReviewRole)) fail(`requestRoleReview[${index}].role is not a management role`);
    text(entry['reason'], `requestRoleReview[${index}].reason`, L.reviewReasonChars);
  }
  const expected = correlationFor(value as unknown as InterpretationMaterial);
  if (value['correlationId'] !== expected) fail('correlationId does not match the interpretation content');
  if (JSON.stringify(value).length > L.serializedChars) fail(`Supervisor AI interpretation exceeds ${L.serializedChars} characters`);
}

/** The report-stream key of a role's AI perspective: never the deterministic report's own key. */
export function supervisorAiStreamId(role: SupervisorAiRole): string {
  return `supervisor:${role}:ai`;
}

function clipLine(value: string, max: number): string {
  const line = value.replace(/\s+/g, ' ').trim();
  return line.length <= max ? line : `${line.slice(0, max - 1)}…`;
}

export function supervisorAiInterpretationToEnvelope(interpretation: SupervisorAiInterpretation, streamId: string): ReportEnvelope {
  assertSupervisorAiInterpretationWellFormed(interpretation);
  text(streamId, 'streamId', 200);
  return {
    streamId,
    reportKind: 'supervisor_synthesis',
    payloadVersion: ENVELOPE_PAYLOAD_VERSION,
    summary: clipLine(`${interpretation.supervisor.role} AI perspective (advisory): ${interpretation.interpretation}`, 1_000),
    payload: {
      protocol: SUPERVISOR_AI_PROTOCOL,
      interpretationVersion: interpretation.interpretationVersion,
      correlationId: interpretation.correlationId,
      supervisorId: supervisorAiStreamId(interpretation.role),
      supervisorRole: interpretation.supervisor.role,
      reportCorrelationId: interpretation.reportCorrelationId,
      confidence: interpretation.confidence,
      executionAuthority: false,
      detail: JSON.stringify(interpretation),
    },
    createdAt: interpretation.createdAt,
  };
}

/** Parse and re-validate an interpretation envelope; the envelope must describe exactly what it carries. */
export function supervisorAiInterpretationFromEnvelope(envelope: unknown): SupervisorAiInterpretation {
  if (!isRecord(envelope) || envelope['reportKind'] !== 'supervisor_synthesis' || envelope['payloadVersion'] !== ENVELOPE_PAYLOAD_VERSION) {
    fail('envelope is not a Supervisor AI interpretation');
  }
  const payload = envelope['payload'];
  if (!isRecord(payload) || payload['protocol'] !== SUPERVISOR_AI_PROTOCOL || typeof payload['detail'] !== 'string') {
    fail('envelope is not a Supervisor AI interpretation');
  }
  let parsed: unknown;
  try { parsed = JSON.parse(payload['detail']); } catch { fail('Supervisor AI interpretation detail must be JSON'); }
  assertSupervisorAiInterpretationWellFormed(parsed);
  if (
    payload['executionAuthority'] !== false
    || payload['interpretationVersion'] !== parsed.interpretationVersion
    || payload['correlationId'] !== parsed.correlationId
    || payload['supervisorId'] !== supervisorAiStreamId(parsed.role)
    || payload['supervisorRole'] !== parsed.supervisor.role
    || payload['reportCorrelationId'] !== parsed.reportCorrelationId
    || payload['confidence'] !== parsed.confidence
    || envelope['createdAt'] !== parsed.createdAt
  ) {
    fail('Supervisor AI interpretation envelope does not describe its detail');
  }
  return parsed;
}

export function isSupervisorAiEnvelope(envelope: unknown): boolean {
  return isRecord(envelope) && isRecord(envelope['payload']) && envelope['payload']['protocol'] === SUPERVISOR_AI_PROTOCOL;
}
